import React, { useState, useEffect } from "react";
import { Table, Button, Input, Space, Tag, Modal, Form, Switch, notification, Popconfirm, Radio, Typography, Divider } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { IconLanguage, IconCircleCheck, IconPlus, IconAlertCircle, IconWorld } from '@tabler/icons-react';
import PreferenceServ from "../../service/PreferenceServ";
import Breadcrumbs from "../../component/Breadcrumbs";
import { usePreference } from "../../context/PreferenceContext";
import EmptyState from "../../util/EmptyState";
import GlobalTooltip from "../../component/GlobalTooltip";
import { PrimaryButton } from "../../component/ui/ActionButtons";

const { Title, Text } = Typography;

const ManageLanguage = () => {
    const { t } = usePreference();
    const [languages, setLanguages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentLang, setCurrentLang] = useState(null);
    const [form] = Form.useForm();

    const fetchLanguages = async () => {
        try {
            setLoading(true);
            const res = await PreferenceServ.getLanguages({ all: 'true' });
            setLanguages(res || []);
        } catch (error) {
            console.error("Failed to fetch languages", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLanguages();
    }, []);

    const handleToggleStatus = async (record) => {
        try {
            // Cannot deactivate a default language
            if (record.isDefault && record.isActive) {
                notification.warning({
                    message: t('cannot_deactivate_default'),
                    description: t('set_another_default_first')
                });
                return;
            }

            const newStatus = !record.isActive;
            await PreferenceServ.updateLanguage(record._id, { isActive: newStatus });
            notification.success({ message: t('status_updated') });
            fetchLanguages();
        } catch (error) {
            notification.error({ message: t('operation_failed') });
        }
    };

    const handleSetDefault = async (record) => {
        try {
            // Must be active to be default
            if (!record.isActive) {
                notification.warning({ message: t('activate_before_default') });
                return;
            }

            if (record.isDefault) return;

            await PreferenceServ.updateLanguage(record._id, { isDefault: true, isActive: true });
            notification.success({ message: t('default_language_updated') });
            fetchLanguages();
        } catch (error) {
            notification.error({ message: t('operation_failed') });
        }
    };

    const handleAdd = () => {
        setIsEditing(false);
        setCurrentLang(null);
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setIsEditing(true);
        setCurrentLang(record);
        setIsModalVisible(true);
    };

    // Use effect to sync form values when modal opens or currentLang changes
    useEffect(() => {
        if (isModalVisible) {
            if (isEditing && currentLang) {
                // Force boolean values for switches to ensure they display correctly
                const normalized = {
                    ...currentLang,
                    isActive: currentLang.isActive === true || currentLang.isActive === 1 || currentLang.isActive === 'true',
                    isDefault: currentLang.isDefault === true || currentLang.isDefault === 1 || currentLang.isDefault === 'true',
                };
                form.setFieldsValue(normalized);
            } else {
                form.resetFields();
                form.setFieldsValue({ isActive: true, isDefault: false });
            }
        }
    }, [isModalVisible, isEditing, currentLang, form]);

    // Handle Default Switch change in Modal
    const onDefaultChange = (checked) => {
        if (checked) {
            form.setFieldsValue({ isActive: true });
        }
    };

    const handleDelete = async (id) => {
        try {
            await PreferenceServ.deleteLanguage(id);
            notification.success({ message: t('language_deleted') });
            fetchLanguages();
        } catch (error) {
            notification.error({ message: t('failed_delete_language') });
        }
    };

    const handleSubmit = async (values) => {
        try {
            // If setting as default, ensure it's also active
            if (values.isDefault) {
                values.isActive = true;
            }

            if (isEditing && currentLang) {
                await PreferenceServ.updateLanguage(currentLang._id, values);
                notification.success({ message: t('language_updated') });
            } else {
                await PreferenceServ.addLanguage(values);
                notification.success({ message: t('language_added') });
            }
            setIsModalVisible(false);
            fetchLanguages();
        } catch (error) {
            notification.error({ message: t('operation_failed'), description: error.message });
        }
    };

    const columns = [
        { title: t('name'), dataIndex: "name", key: "name" },
        { title: t('code'), dataIndex: "code", key: "code" },
        {
            title: (
                <GlobalTooltip title={t('system_default_tooltip')}>
                    {t('system_default')}
                </GlobalTooltip>
            ),
            key: "isDefault",
            align: 'center',
            render: (_, record) => (
                <Radio
                    checked={record.isDefault}
                    onChange={() => handleSetDefault(record)}
                />
            )
        },
        {
            title: t('active_status_label'),
            key: "isActive",
            align: 'center',
            render: (_, record) => (
                <Switch
                    checked={record.isActive}
                    onChange={() => handleToggleStatus(record)}
                    disabled={record.isDefault}
                />
            )
        },
        {
            title: t('actions'),
            key: "actions",
            render: (_, record) => (
                <Space>
                    <GlobalTooltip title={t('edit')}>
                        <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    </GlobalTooltip>
                    {!record.isDefault && (
                        <Popconfirm title={t('confirm_delete_language')} onConfirm={() => handleDelete(record._id)}>
                            <Button danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div className="sd-container" style={{ padding: '24px' }}>
            <div className="pcu-margin-bottom-20">
                <Breadcrumbs />
            </div>

            <div className="sd-header" style={{ marginBottom: '32px', background: 'transparent', padding: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ 
                        width: '56px', 
                        height: '56px', 
                        background: 'linear-gradient(135deg, #4f46e5 0%, #06b6d4 100%)', 
                        borderRadius: '16px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        boxShadow: '0 8px 16px rgba(79, 70, 229, 0.2)' 
                    }}>
                        <IconLanguage size={28} color="white" />
                    </div>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: '#1e293b', letterSpacing: '-0.3px' }}>
                            {t('manage_language')}
                        </h1>
                        <p style={{ margin: 0, fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                            {languages.length} {t('languages_configured')}
                        </p>
                    </div>
                </div>
                <PrimaryButton 
                    onClick={handleAdd} 
                    text={t('add_language')} 
                    icon={<IconPlus size={18} />} 
                    style={{
                        height: '50px',
                        padding: '0 24px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }}
                />
            </div>

            <div className="main-content-card pcu-bg-white pcu-radius-24 pcu-shadow-sm pcu-overflow-hidden" style={{ border: '1px solid #f1f5f9' }}>
                <div className="pcu-padding-24">
                <Table
                    columns={columns}
                    dataSource={languages}
                    rowKey="_id"
                    className="premium-table"
                    locale={{
                        emptyText: (
                            <EmptyState
                                title={t('no_languages_found')}
                                description={t('no_languages_description')}
                                gradient="blue"
                                onAction={handleAdd}
                                actionText={t('add_language')}
                                icon="inbox"
                            />
                        )
                    }}
                    loading={loading}
                    pagination={{
                        position: ['bottomRight'],
                        showTotal: (total) => <span className="pagination-total">Total {total} items</span>,
                        className: "premium-pagination"
                    }}
                />

                <Modal
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '32px', height: '32px', background: '#eef2ff', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <IconWorld size={20} color="#4f46e5" />
                            </div>
                            <span style={{ fontWeight: 800, color: '#1e293b' }}>
                                {isEditing ? t('edit_language') : t('add_language')}
                            </span>
                        </div>
                    }
                    open={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    onOk={() => form.submit()}
                    okText={isEditing ? t('update') : t('save')}
                    cancelText={t('cancel')}
                    destroyOnClose
                    centered
                    width={500}
                    okButtonProps={{
                        style: { 
                            height: '44px', 
                            borderRadius: '10px', 
                            fontWeight: 700, 
                            padding: '0 24px',
                            background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                            border: 'none'
                        }
                    }}
                    cancelButtonProps={{
                        style: { height: '44px', borderRadius: '10px', fontWeight: 600 }
                    }}
                >
                    <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: '20px' }}>
                        <Form.Item name="name" label={<span style={{ fontWeight: 700, color: '#475569' }}>{t('language_name')}</span>} rules={[{ required: true, message: t('please_enter_name') }]}>
                            <Input placeholder={t('language_name_placeholder')} style={{ height: '48px', borderRadius: '12px' }} />
                        </Form.Item>
                        <Form.Item name="code" label={<span style={{ fontWeight: 700, color: '#475569' }}>{t('language_code')}</span>} rules={[{ required: true, message: t('please_enter_code') }]}>
                            <Input placeholder={t('language_code_placeholder')} style={{ height: '48px', borderRadius: '12px' }} />
                        </Form.Item>

                        <div style={{ 
                            background: '#f8fafc', 
                            padding: '24px', 
                            borderRadius: '16px', 
                            marginTop: '12px',
                            border: '1px solid #f1f5f9'
                        }}>
                            {/* Force active if default */}
                            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.isDefault !== currentValues.isDefault}>
                                {({ getFieldValue }) => {
                                    const isDefault = getFieldValue('isDefault');
                                    return (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                            <div>
                                                <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>{t('active_status_label')}</div>
                                                <div style={{ fontSize: '12px', color: '#64748b' }}>{t('allow_users_to_select')}</div>
                                            </div>
                                            <Form.Item name="isActive" valuePropName="checked" noStyle>
                                                <Switch
                                                    disabled={isDefault}
                                                    style={{ background: isDefault ? '#4f46e5' : undefined }}
                                                />
                                            </Form.Item>
                                        </div>
                                    );
                                }}
                            </Form.Item>

                            <Divider style={{ margin: '16px 0' }} />

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>{t('set_default_language')}</div>
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>{t('default_for_new_sessions')}</div>
                                </div>
                                <Form.Item name="isDefault" valuePropName="checked" noStyle>
                                    <Switch
                                        onChange={onDefaultChange}
                                    />
                                </Form.Item>
                            </div>
                        </div>
                    </Form>
                </Modal>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                .premium-table .ant-table {
                    background: transparent !important;
                }
                .premium-table .ant-table-thead > tr > th {
                    background: #f8fafc !important;
                    color: #64748b !important;
                    font-weight: 700 !important;
                    text-transform: uppercase !important;
                    font-size: 11px !important;
                    letter-spacing: 0.5px !important;
                    padding: 16px 24px !important;
                    border-bottom: 2px solid #f1f5f9 !important;
                }
                .premium-table .ant-table-tbody > tr > td {
                    padding: 16px 24px !important;
                    border-bottom: 1px solid #f1f5f9 !important;
                    color: #1e293b !important;
                    font-weight: 500 !important;
                }
                .premium-table .ant-table-tbody > tr:hover > td {
                    background: #fdfdfd !important;
                }
                .premium-pagination {
                    margin: 24px 0 0 0 !important;
                }
                .ant-radio-checked .ant-radio-inner {
                    border-color: #4f46e5 !important;
                    background-color: #4f46e5 !important;
                }
                .ant-switch-checked {
                    background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%) !important;
                }
                .ant-input:focus, .ant-input-focused {
                    border-color: #4f46e5 !important;
                    box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.1) !important;
                }
            `}} />
        </div>
    );
};

export default ManageLanguage;

